import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { API_Search, UserMinimal } from "../../../src/apidefs";

export const UserSearchInput = ({
  defaultValue,
  placeholder,
  testId,
  onValueChange,
}: {
  defaultValue: string;
  placeholder: string;
  testId?: string;
  onValueChange?: (value: string) => void;
}) => {
  const [value, setValue] = useState(defaultValue);
  const [results, setResults] = useState<API_Search | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showDropdown]);

  const search = async (query: string) => {
    if (query.trim() === "") {
      setResults(null);
      setShowDropdown(false);
      return;
    }

    const result = await api.get<"/api/search/:search">(
      `/api/search/${encodeURIComponent(query)}`,
      { params: { types: "users" } }
    );

    if (!api.isToast(result)) {
      setResults(result);
      setShowDropdown(true);
      setSelectedIndex(0);
    }
  };

  const selectUser = (user: UserMinimal) => {
    setValue(user.username);
    onValueChange?.(user.username);
    setShowDropdown(false);
    setResults(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || !results) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, Math.min(results.users.length, 5) - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results.users[selectedIndex]) {
        selectUser(results.users[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onValueChange?.(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => {
          if (results) {
            setShowDropdown(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
      />

      {showDropdown &&
        results &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            className="z-50 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-white/10 dark:bg-slate-800"
          >
            {results.users.slice(0, 5).map((user, index) => (
              <button
                key={user.username}
                type="button"
                onClick={() => selectUser(user)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left transition ${
                  index === selectedIndex
                    ? "bg-chips-gold/10 dark:bg-chips-gold/20"
                    : "hover:bg-slate-100 dark:hover:bg-white/5"
                }`}
              >
                <img
                  src={user.avatar_url || "https://chipscompo.com/img/avatar.png"}
                  className="h-8 w-8 rounded-full object-cover"
                  alt=""
                />
                <span className="font-medium text-slate-900 dark:text-white">
                  {user.username}
                </span>
              </button>
            ))}
            <a
              href="/registry"
              className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm font-medium text-sky-600 transition hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 ${results.users.length > 0 ? "border-t border-slate-200 dark:border-white/10" : ""}`}
            >
              See all users &rarr;
            </a>
          </div>,
          document.body
        )}
    </div>
  );
};
